/*
 * Copyright (c) 2022, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
} from "@material-ui/core";
import React, { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import GraphiQLExplorer from "graphiql-explorer";
import {
  buildClientSchema,
  getIntrospectionQuery,
  GraphQLSchema,
} from "graphql";
import { FormattedMessage, useIntl } from "react-intl";
import { useStyles } from "./styles";
import "graphiql/graphiql.css";
import GraphiQL from "graphiql";
import { truncateTextMiddle } from "../../../../data/utils";
import InfoTooltip from "../../../../elements/CustomTooltip/InfoTooltip";
import { APPLICATION_JSON, CONTENT_TYPE } from "../../../../data/constants";
import InfoBanner from "../../../shared/InfoBanner";

export interface GraphiqlViewerProps {
  token: string;
  apiGatewayUrl: string;
  securitySchemeType: string;
  authorizationHeader: string;
  initialEndpoint?: string;
}

export default function GraphiqlViewer(props: GraphiqlViewerProps) {
  const {
    token,
    apiGatewayUrl,
    securitySchemeType,
    authorizationHeader,
    initialEndpoint,
  } = props;

  const classes = useStyles();
  const [query, setQuery] = useState("");
  const [endpoint, setEndpoint] = useState(initialEndpoint || "/");
  const [isExplorerOpen, setIsExplorerOpen] = useState(false);
  const graphiqlEl = useRef<any>();
  const intl = useIntl();

  const schemaFetchError = intl.formatMessage({
    id: "components.api.ApiTryout.GraphiqlViewer.inputError.schemaFetchError",
    defaultMessage: "Failed to fetch GraphQL schema",
  });

  const getAuthHeader = (): string => {
    let headerValue = "";
    if (authorizationHeader === "apikey") {
      headerValue = token;
    } else if (securitySchemeType === "BASIC") {
      headerValue = "Basic " + token;
    } else if (securitySchemeType === "TEST") {
      headerValue = token;
    } else {
      headerValue = "Bearer " + token;
    }

    return headerValue;
  };

  const getSchema = async (url: string) => {
    const result = await fetch(url, {
      method: "post",
      headers: {
        [CONTENT_TYPE]: APPLICATION_JSON,
        [authorizationHeader]: getAuthHeader(),
      },
      body: JSON.stringify({ query: getIntrospectionQuery() }),
    });
    if (result.ok) {
      try {
        const schemaResponse = await result.json();

        // eslint-disable-next-line no-underscore-dangle
        if (schemaResponse?.data?.__schema) {
          return buildClientSchema(schemaResponse.data);
        }
        throw new Error(
          schemaResponse?.error_message ||
            schemaResponse?.message ||
            'Invalid response received'
        );
      } catch {
        throw new Error('Invalid response received');
      }
    } else if (result.status === 404) {
      throw new Error('Request URL not found');
    } else {
      throw new Error(
        `Unexpected HTTP status code ${result.status} received`
      );
    }
  };

  const {
    data: schema,
    isFetching: fetchingSchema,
    isError,
    error,
    refetch,
    dataUpdatedAt,
  } = useQuery<GraphQLSchema | null, Error>(
    ["getIntrospectionQuery", { apiGatewayUrl, token }],
    () => getSchema(`${apiGatewayUrl}${endpoint}`),
    {
      enabled: !!token,
    }
  );

  return (
    <>
      {token ? (
        <Box className={classes.graphqlWrapper}>
          <Box className="graphiql-container">
            <GraphiQLExplorer
              schema={schema}
              query={query}
              onEdit={setQuery}
              explorerIsOpen={isExplorerOpen}
              onToggleExplorer={() => setIsExplorerOpen(!isExplorerOpen)}
            />
            <GraphiQL
              key={`graphiQl-${dataUpdatedAt}`}
              ref={graphiqlEl}
              fetcher={(param, opts) =>
                fetch(`${apiGatewayUrl}${endpoint}`, {
                  method: "post",
                  body: JSON.stringify(param),
                  headers: opts?.headers,
                }).then((response) => response.json())
              }
              schema={schema}
              query={query}
              onEditQuery={(editQuery) => setQuery(editQuery || "")}
              readOnly={!schema}
              headers={JSON.stringify({
                [CONTENT_TYPE]: APPLICATION_JSON,
                [authorizationHeader]: getAuthHeader(),
              })}
            >
              <GraphiQL.Toolbar>
                <Button
                  onClick={() => graphiqlEl?.current?.ref?.props?.prettify()}
                  disabled={!schema || isError}
                  classes={{root: classes.greyOutlineBtnRoot, disabled: classes.disabledBtn}}
                  className={classes.greyOutlinedButton}
                >
                  <FormattedMessage
                    id="components.api.ApiTryout.GraphiqlViewer.toolbarButtons.prettify"
                    defaultMessage="Prettify"
                  />
                </Button>
                <Button
                  onClick={() => graphiqlEl?.current?.ref?.props?.historyContext?.toggle()}
                  disabled={!schema || isError}
                  classes={{root: classes.greyOutlineBtnRoot, disabled: classes.disabledBtn}}
                  className={classes.greyOutlinedButton}
                >
                  <FormattedMessage
                    id="components.api.ApiTryout.GraphiqlViewer.toolbarButtons.history"
                    defaultMessage="History"
                  />
                </Button>
                <Button
                  onClick={() => setIsExplorerOpen(!isExplorerOpen)}
                  disabled={!schema || isError}
                  classes={{root: classes.greyOutlineBtnRoot, disabled: classes.disabledBtn}}
                  className={classes.greyOutlinedButton}
                >
                  <FormattedMessage
                    id="components.api.ApiTryout.GraphiqlViewer.toolbarButtons.explorer"
                    defaultMessage="Explorer"
                  />
                </Button>
                <Box ml={1}>
                  <InfoTooltip
                    content={error?.message}
                    heading={schemaFetchError}
                    isDisabled={!isError}
                    darken
                  >
                    <TextField
                      className={clsx(
                        classes.endpointTextField,
                        isError && classes.endpointTextFieldError
                      )}
                      defaultValue={endpoint}
                      onChange={(event) => setEndpoint(event.target.value)}
                      InputProps={{
                        disableUnderline: true,
                        startAdornment: (
                          <InputAdornment position="start">
                            {truncateTextMiddle(apiGatewayUrl, 60, "......")}
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            {fetchingSchema && <CircularProgress size={20} />}
                          </InputAdornment>
                        ),
                      }}
                      onKeyPress={(event: { key: string }) => {
                        if (event.key === "Enter") {
                          refetch();
                        }
                      }}
                      onBlur={() => refetch()}
                    />
                  </InfoTooltip>
                </Box>
              </GraphiQL.Toolbar>
            </GraphiQL>
          </Box>
        </Box>
      ) : (
        <Box pl={1.5}>
          <InfoBanner 
            message={intl.formatMessage({
              id: "components.api.ApiTryout.GraphiqlViewer.toolbarButtons.generateTokenInfo",
              defaultMessage: "Generate a test key to try out the API",
            })}
            testId="generate-token-info"
          />
        </Box>
      )}
    </>
  );
}
